import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usersAPI, conversationsAPI } from '../services/api'
import {
  ArrowLeft, MapPin, Star, User, Calendar,
  MessageCircle, Eye, Award, Instagram,
  Youtube, Twitter, Globe, Package,
  CheckCircle, Heart, Zap, Target, Video,
  Play, ExternalLink, Briefcase, Clock, TrendingUp, Plus
} from 'lucide-react'
import InviteCreatorModal from '../components/InviteCreatorModal'
import PublicHeader from '../components/PublicHeader'
import PortfolioVideo from '../components/PortfolioVideo'

// Derive a real preview source from a portfolio item.
// Returns { kind: 'image' | 'video' | 'none', src }
const getPortfolioPreview = (item) => {
  if (!item) return { kind: 'none' }
  if (item.thumbnailUrl) return { kind: 'image', src: item.thumbnailUrl }
  const url = item.videoUrl || ''
  if (!url) return { kind: 'none' }
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,15})/)
  if (yt) return { kind: 'image', src: `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg` }
  if (/\.(mp4|webm|mov|m4v|ogv)(\?|#|$)/i.test(url)) {
    return { kind: 'video', src: url }
  }
  return { kind: 'video', src: url }
}

// getPortfolioEmbed staat in utils/portfolioEmbed.js — zie daar de toelichting
// waarom portfolio-items nooit als uitgaande link worden gerenderd.

const formatFollowers = (n) => n >= 1000000 ? (n / 1000000).toFixed(1).replace('.0', '') + 'M' : n >= 1000 ? (n / 1000).toFixed(1).replace('.0', '') + 'K' : String(n)

const CreatorDetailPage = ({ creatorId: propCreatorId, onNavigate, onBack } = {}) => {
  const params = useParams()
  const id = propCreatorId || params.id
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [creator, setCreator] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [startingChat, setStartingChat] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)

  useEffect(() => {
    fetchCreator()
  }, [id])

  const fetchCreator = async () => {
    try {
      setLoading(true)
      const response = await usersAPI.getCreator(id)
      setCreator(response.data.creator || response.data)
    } catch (error) {
      console.error('Error fetching creator:', error)
      setError('Creator niet gevonden')
    } finally {
      setLoading(false)
    }
  }

  const handleStartChat = async (pkg = null) => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    try {
      setStartingChat(true)
      const response = await conversationsAPI.createConversation({
        participantId: id
      })

      // Backend retourneert { conversation: { id, ... } } — gebruik `id`, niet `_id`
      const conversationId = response.data.conversation?.id
        || response.data.conversation?._id
        || response.data.id
        || response.data._id

      // Als er een pakket is meegegeven: stuur direct een geformatteerd vraagbericht.
      // Type-guard: alleen echte pakket-objecten (niet React event-objects).
      const isPackage = pkg && typeof pkg === 'object' && typeof pkg.name === 'string' && typeof pkg.price === 'number'
      if (isPackage && conversationId) {
        const includedLabels = {
          muziek: 'Muziek', script_briefing: 'Script/Briefing', ruwe_content: 'Ruwe content',
          video_editing: 'Video editing', ondertiteling: 'Ondertiteling', voice_over: 'Voice-over',
          productfotos: "Productfoto's", thumbnail: 'Thumbnail'
        }
        const included = (pkg.includedItems || []).map(i => includedLabels[i] || i).join(', ')
        const creatorFirstName = creator?.firstName || creator?.displayName?.split(' ')[0] || 'daar'
        const lines = [
          `👋 Hi ${creatorFirstName}, ik heb interesse in je pakket "${pkg.name}" (€${pkg.price} excl. BTW).`,
          ``,
          `📦 Pakket dat ik bekeek:`,
          `• ${pkg.videoCount || 1}× video van ${pkg.videoLength || '30s'}`,
          `• Levertijd: ${pkg.deliveryDays || '6-8 dagen'}`,
          `• ${pkg.revisions ?? 1} revisie(s) · ${pkg.hooks ?? 1} hook(s)`,
          included ? `• Inclusief: ${included}` : null,
          ``,
          `Kan je me wat meer vertellen voordat ik bestel? Of zal ik via "Bestel pakket" direct doorgaan?`
        ].filter(Boolean).join('\n')

        try {
          await conversationsAPI.sendMessage(conversationId, { content: lines })
        } catch (msgErr) {
          console.error('Kon pakket-context bericht niet sturen:', msgErr)
        }
      }

      onNavigate ? onNavigate('messages') : navigate(`/messages?conversation=${conversationId}`)
    } catch (error) {
      console.error('Error starting chat:', error)
      alert('Er is een fout opgetreden bij het starten van een gesprek')
    } finally {
      setStartingChat(false)
    }
  }

  const getSocialIcon = (platform) => {
    const svgIcons = {
      instagram: () => (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
          <defs>
            <radialGradient id="ig-grad" cx="30%" cy="107%" r="150%">
              <stop offset="0%" stopColor="#fdf497"/>
              <stop offset="5%" stopColor="#fdf497"/>
              <stop offset="45%" stopColor="#fd5949"/>
              <stop offset="60%" stopColor="#d6249f"/>
              <stop offset="90%" stopColor="#285AEB"/>
            </radialGradient>
          </defs>
          <rect x="2" y="2" width="20" height="20" rx="6" stroke="url(#ig-grad)" strokeWidth="2"/>
          <circle cx="12" cy="12" r="4.5" stroke="url(#ig-grad)" strokeWidth="2"/>
          <circle cx="17.5" cy="6.5" r="1.5" fill="url(#ig-grad)"/>
        </svg>
      ),
      tiktok: () => (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.48a8.1 8.1 0 004.76 1.52V7.56a4.84 4.84 0 01-1-.87z" fill="#000"/>
        </svg>
      ),
      youtube: () => (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path d="M23.5 6.19a3.02 3.02 0 00-2.12-2.14C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.55A3.02 3.02 0 00.5 6.19 31.6 31.6 0 000 12a31.6 31.6 0 00.5 5.81 3.02 3.02 0 002.12 2.14c1.88.55 9.38.55 9.38.55s7.5 0 9.38-.55a3.02 3.02 0 002.12-2.14A31.6 31.6 0 0024 12a31.6 31.6 0 00-.5-5.81z" fill="#FF0000"/>
          <path d="M9.75 15.02l6.25-3.02-6.25-3.02v6.04z" fill="#fff"/>
        </svg>
      ),
      twitter: () => (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="#000"/>
        </svg>
      )
    }
    if (svgIcons[platform]) return svgIcons[platform]
    return () => <Globe className="w-5 h-5 text-[#475569]" />
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#3EB489] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#475569]">Creator laden...</p>
        </div>
      </div>
    )
  }

  if (error || !creator) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Creator niet gevonden</h2>
          <p className="text-[#475569] mb-6">{error}</p>
          {onBack ? (
            <button onClick={onBack} className="text-[#3EB489] hover:underline">
              Terug naar creators
            </button>
          ) : (
            <Link to="/creators" className="text-[#3EB489] hover:underline">
              Terug naar creators
            </Link>
          )}
        </div>
      </div>
    )
  }

  const hasPackages = creator.packages && creator.packages.length > 0
  const minPackagePrice = hasPackages ? Math.min(...creator.packages.map(p => p.price)) : null
  const fullName = creator.firstName && creator.lastName
    ? `${creator.firstName} ${creator.lastName}`
    : creator.displayName || 'Naamloze Creator'
  const isAvailable = !creator.availability || creator.availability === 'immediately' || creator.availability?.status === 'Beschikbaar' || creator.availability === 'within-week'
  const isCompanyOrAgency = isAuthenticated && (user?.role === 'bedrijf' || user?.role === 'agency')
  // Totaal bereik over alle platforms — één cijfer waarop bedrijven kunnen scannen
  const totalFollowers = Object.values(creator.socialMedia || {})
    .reduce((sum, d) => sum + (d?.followers || d?.subscribers || 0), 0)

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]">
      {/* Header - only show in standalone mode */}
      {!propCreatorId && <PublicHeader />}

      <div className={`max-w-7xl mx-auto px-4 md:px-6 ${propCreatorId ? 'pt-4 pb-8' : 'pt-6 md:pt-10 pb-28 lg:pb-20'}`}>
        {/* Back Button */}
        {onBack ? (
          <button onClick={onBack} className="inline-flex items-center text-[#475569] hover:text-[#3EB489] transition mb-4 md:mb-6 text-sm">
            <ArrowLeft className="w-4 h-4 mr-1.5" />Terug naar creators
          </button>
        ) : (
          <Link to="/creators" className="inline-flex items-center text-[#475569] hover:text-[#3EB489] transition mb-4 md:mb-6 text-sm">
            <ArrowLeft className="w-4 h-4 mr-1.5" />Terug naar creators
          </Link>
        )}

        {/* ============ HERO CARD — volle breedte ============ */}
        <div className="mb-4 md:mb-6 rounded-2xl md:rounded-3xl bg-white border border-gray-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-6 md:p-8">
          <div className="flex flex-col sm:flex-row gap-5 md:gap-7">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {creator.profilePictureUrl ? (
                <img
                  src={creator.profilePictureUrl}
                  alt={fullName}
                  className="w-24 h-24 md:w-28 md:h-28 rounded-2xl object-cover ring-1 ring-gray-200"
                />
              ) : (
                <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-[#3EB489] flex items-center justify-center ring-1 ring-gray-200">
                  <User className="w-12 h-12 md:w-14 md:h-14 text-white" />
                </div>
              )}
            </div>

            {/* Identity */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-2xl md:text-3xl font-bold leading-tight tracking-tight">{fullName}</h1>
                {creator.isAmbassador && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#F59E0B]/30 text-[#B45309] text-[11px] font-semibold">
                    <Award className="w-3.5 h-3.5" />Ambassadeur
                  </span>
                )}
                {isAvailable && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#3EB489]/30 text-[#3EB489] text-[11px] font-semibold">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#3EB489] opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#3EB489]"></span>
                    </span>
                    Beschikbaar
                  </span>
                )}
              </div>

              {/* Kerngegevens */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm text-[#475569]">
                {creator.location && (creator.location.city || creator.location.country) && (
                  <div className="inline-flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-[#94A3B8]" />
                    <span>{creator.location.city}{creator.location.city && creator.location.country && ', '}{creator.location.country}</span>
                  </div>
                )}
                {creator.stats?.averageRating > 0 && (
                  <div className="inline-flex items-center gap-1.5">
                    <Star className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" />
                    <span className="font-semibold text-[#0F172A]">{creator.stats.averageRating.toFixed(1)}</span>
                  </div>
                )}
                {totalFollowers > 0 && (
                  <div className="inline-flex items-center gap-1.5">
                    <TrendingUp className="w-4 h-4 text-[#94A3B8]" />
                    <span><strong className="font-semibold text-[#0F172A]">{formatFollowers(totalFollowers)}</strong> volgers</span>
                  </div>
                )}
                {creator.stats?.completedProjects > 0 && (
                  <div className="inline-flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4 text-[#94A3B8]" />
                    <span><strong className="font-semibold text-[#0F172A]">{creator.stats.completedProjects}</strong> opdrachten voltooid</span>
                  </div>
                )}
                {hasPackages && (
                  <div className="inline-flex items-center gap-1.5">
                    <Package className="w-4 h-4 text-[#94A3B8]" />
                    <span>Pakketten vanaf <strong className="font-semibold text-[#0F172A]">€{minPackagePrice}</strong></span>
                  </div>
                )}
              </div>

              {creator.niches && creator.niches.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-5">
                  {creator.niches.map((niche, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-lg border border-gray-200 text-[11px] font-medium text-[#475569] capitalize">
                      {niche}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Bio compact in hero */}
          {creator.bio && (
            <div className="mt-7 pt-7 border-t border-gray-100">
              <h2 className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-3">Over {creator.firstName || 'de creator'}</h2>
              <p className="text-sm md:text-[15px] text-[#1F2937] leading-relaxed whitespace-pre-wrap">
                {creator.bio}
              </p>
            </div>
          )}

          {/* ===== Acties — één duidelijke primaire knop, rest rustig ===== */}
          <div className="mt-7 pt-7 border-t border-gray-100 flex flex-col sm:flex-row flex-wrap gap-2.5">
            <button
              onClick={handleStartChat}
              disabled={startingChat}
              className={`${propCreatorId ? 'inline-flex' : 'hidden lg:inline-flex'} items-center justify-center gap-2 px-6 py-3.5 bg-[#3EB489] hover:bg-[#359E78] disabled:opacity-60 text-white rounded-xl font-semibold text-sm transition shadow-sm shadow-[#3EB489]/20`}
            >
              <MessageCircle className="w-4 h-4" />
              {startingChat ? 'Chat starten...' : 'Start gesprek'}
            </button>

            {isCompanyOrAgency && (
              <>
                <button
                  onClick={() => navigate(`/projects/create?inviteCreator=${id}`)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-gray-200 text-[#0F172A] hover:border-[#3EB489] hover:text-[#3EB489] font-semibold text-sm transition"
                  title={`Nieuwe opdracht aanmaken voor ${creator.firstName || creator.displayName || 'deze creator'}`}
                >
                  <Plus className="w-4 h-4" />
                  Nieuwe opdracht
                </button>
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-gray-200 text-[#0F172A] hover:border-[#3EB489] hover:text-[#3EB489] font-semibold text-sm transition"
                  title="Kies een bestaande opdracht om voor te stellen"
                >
                  <Briefcase className="w-4 h-4" />Bestaande opdracht
                </button>
              </>
            )}

            {hasPackages && (
              <a
                href="#packages"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-gray-200 text-[#0F172A] hover:border-[#3EB489] hover:text-[#3EB489] font-semibold text-sm transition"
              >
                <Package className="w-4 h-4" />
                {creator.packages.length} pakket{creator.packages.length === 1 ? '' : 'ten'} vanaf €{minPackagePrice}
              </a>
            )}
          </div>
        </div>

        {/* ============ 2-COL CONTENT ============ */}
        <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
          {/* MAIN COLUMN */}
          <div className="lg:col-span-2 space-y-4 md:space-y-6">
            {/* Portfolio — eerst, prominent */}
            <div className="p-5 md:p-7 rounded-2xl bg-white border border-gray-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="flex items-center gap-2.5 mb-5 md:mb-6">
                <Video className="w-5 h-5 text-[#3EB489]" />
                <h2 className="text-lg md:text-xl font-bold tracking-tight">Portfolio</h2>
                {creator.portfolio && creator.portfolio.length > 0 && (
                  <span className="px-2 py-0.5 rounded-lg border border-gray-200 text-[11px] font-medium text-[#475569]">
                    {creator.portfolio.length} {creator.portfolio.length === 1 ? 'item' : 'items'}
                  </span>
                )}
              </div>
              {creator.portfolio && creator.portfolio.length > 0 ? (
                (() => {
                  // Dynamische grid: vermijd lelijke "rest-rij" met 1 item
                  // 1 → 1col · 2 → 2col · 3 → 3col · 4 → 2col (2+2) · 5+ → 3col
                  const n = creator.portfolio.length
                  const gridCls = n === 1
                    ? 'grid-cols-1 max-w-md mx-auto'
                    : n === 2
                      ? 'grid-cols-1 sm:grid-cols-2'
                      : n === 3
                        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                        : n === 4
                          ? 'grid-cols-1 sm:grid-cols-2'
                          : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                  return (
                <div className={`grid ${gridCls} gap-3 md:gap-4`}>
                  {creator.portfolio.map((item, index) => {
                    return (
                      <div key={index} className="rounded-xl border border-gray-200 overflow-hidden hover:border-[#3EB489]/40 hover:shadow-[0_14px_32px_-20px_rgba(15,23,42,0.3)] transition-all duration-200 group/pi">
                        <div className="relative aspect-video bg-gray-100 overflow-hidden">
                          <PortfolioVideo item={item} />
                        </div>
                        <div className="p-4">
                          {item.category && (
                            <span className="inline-block px-2 py-0.5 rounded-lg border border-gray-200 text-[11px] font-medium text-[#475569] capitalize">
                              {item.category}
                            </span>
                          )}
                          {item.title && (
                            <h3 className="font-semibold text-sm leading-snug mt-2.5">{item.title}</h3>
                          )}
                          {item.description && (
                            <p className="text-xs text-[#475569] leading-relaxed line-clamp-2 mt-2">{item.description}</p>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
                  )
                })()
              ) : (
                <div className="py-12 text-center text-[#94A3B8] rounded-xl border border-dashed border-gray-200">
                  <Video className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">Nog geen portfolio items</p>
                </div>
              )}
            </div>

            {/* Packages */}
            {hasPackages && (
              <div className="p-5 md:p-7 rounded-2xl bg-white border border-gray-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] scroll-mt-24" id="packages">
                <div className="flex items-center gap-2.5 mb-5 md:mb-6">
                  <Package className="w-5 h-5 text-[#3EB489]" />
                  <h2 className="text-lg md:text-xl font-bold tracking-tight">Pakketten</h2>
                  <span className="px-2 py-0.5 rounded-lg border border-gray-200 text-[11px] font-medium text-[#475569]">{creator.packages.length}</span>
                </div>
                {(() => {
                  // Dynamische grid: max 3 pakketten, dus 1=1col, 2=2col, 3=3col
                  const n = creator.packages.length
                  const pkgGrid = n === 1
                    ? 'grid-cols-1 max-w-md mx-auto'
                    : n === 2
                      ? 'grid-cols-1 sm:grid-cols-2'
                      : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                  return (
                <div className={`grid ${pkgGrid} gap-3 md:gap-4`}>
                  {creator.packages.map((pkg, index) => {
                    const includedLabels = {
                      muziek: 'Muziek', script_briefing: 'Script/Briefing', ruwe_content: 'Ruwe content',
                      video_editing: 'Video editing', ondertiteling: 'Ondertiteling', voice_over: 'Voice-over',
                      productfotos: "Productfoto's", thumbnail: 'Thumbnail'
                    }
                    return (
                      <div key={index} className="rounded-xl border border-gray-200 p-5 hover:border-[#3EB489]/50 hover:shadow-[0_14px_32px_-20px_rgba(15,23,42,0.3)] transition-all duration-200 flex flex-col">
                        <h3 className="text-base font-bold">{pkg.name}</h3>
                        <div className="text-2xl font-black tracking-tight text-[#0F172A] mt-3">€{pkg.price}</div>
                        <p className="text-[11px] text-[#94A3B8] mt-1.5">excl. BTW</p>
                        {pkg.description && (
                          <p className="text-sm text-[#475569] leading-relaxed mt-4">{pkg.description}</p>
                        )}
                        <div className="space-y-2.5 text-sm mt-5 pt-5 border-t border-gray-100">
                          <div className="flex justify-between gap-3"><span className="text-[#475569]">Video's</span><span className="font-medium">{pkg.videoCount || 1}×</span></div>
                          <div className="flex justify-between gap-3"><span className="text-[#475569]">Lengte</span><span className="font-medium">{pkg.videoLength || '30s'}</span></div>
                          <div className="flex justify-between gap-3"><span className="text-[#475569]">Levertijd</span><span className="font-medium">{pkg.deliveryDays || '6-8 dagen'}</span></div>
                          <div className="flex justify-between gap-3"><span className="text-[#475569]">Revisies</span><span className="font-medium">{pkg.revisions ?? 1}</span></div>
                          {pkg.hooks > 0 && <div className="flex justify-between gap-3"><span className="text-[#475569]">Hooks</span><span className="font-medium">{pkg.hooks}</span></div>}
                        </div>
                        {pkg.includedItems?.length > 0 && (
                          <div className="mt-5">
                            <p className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-2.5">Inclusief</p>
                            <div className="flex flex-wrap gap-1.5">
                              {pkg.includedItems.map((it, i) => (
                                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-200 text-[11px] text-[#475569]">
                                  <CheckCircle className="w-3 h-3 text-[#3EB489]" />{includedLabels[it] || it}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="mt-auto pt-6 space-y-2">
                          {isCompanyOrAgency && (
                            <button
                              onClick={() => navigate(`/checkout/${id}/${index}`)}
                              className="w-full px-4 py-3 bg-[#3EB489] text-white rounded-xl font-semibold text-sm hover:bg-[#359E78] transition flex items-center justify-center gap-2 shadow-sm shadow-[#3EB489]/20"
                            >
                              <Briefcase className="w-4 h-4" />Bestel pakket
                            </button>
                          )}
                          <button
                            onClick={() => handleStartChat(pkg)}
                            disabled={startingChat}
                            className={`w-full px-4 py-3 rounded-xl font-semibold text-sm transition flex items-center justify-center gap-2 ${
                              isCompanyOrAgency
                                ? 'border border-gray-200 text-[#475569] hover:border-[#3EB489] hover:text-[#3EB489]'
                                : 'bg-[#3EB489] text-white hover:bg-[#359E78] shadow-sm shadow-[#3EB489]/20'
                            }`}
                          >
                            <MessageCircle className="w-4 h-4" />Vraag stellen
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
                  )
                })()}
              </div>
            )}

            {/* Moodboard */}
            {creator.moodboard && creator.moodboard.length > 0 && (
              <div className="p-5 md:p-7 rounded-2xl bg-white border border-gray-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                <div className="flex items-center gap-2.5 mb-5 md:mb-6">
                  <Heart className="w-5 h-5 text-[#7c3aed]" />
                  <h2 className="text-lg md:text-xl font-bold tracking-tight">Moodboard</h2>
                  <span className="px-2 py-0.5 rounded-lg border border-gray-200 text-[11px] font-medium text-[#475569]">
                    {creator.moodboard.length} {creator.moodboard.length === 1 ? 'foto' : "foto's"}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 md:gap-3">
                  {creator.moodboard.map((item, index) => (
                    <div key={index} className="group relative">
                      <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 ring-1 ring-gray-200/70">
                        <img src={item.imageUrl} alt={item.caption || 'Moodboard foto'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        {item.caption && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 md:p-3 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                            <p className="text-white text-xs md:text-sm line-clamp-2">{item.caption}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* SIDEBAR */}
          <aside className="space-y-4 md:space-y-6 lg:sticky lg:top-24 lg:self-start">
            {/* Social Media compact */}
            {creator.socialMedia && Object.keys(creator.socialMedia).length > 0 && (() => {
              const userId = user?._id || user?.id
              const isOwnerView = isAuthenticated && userId && userId === creator.id
              const isAdmin = user?.role === 'admin'
              const canVisitProfiles = isOwnerView || isAdmin
              const visibleEntries = Object.entries(creator.socialMedia).filter(([, data]) => {
                if (!data) return false
                const followers = data.followers || data.subscribers
                return Boolean(followers || data.username || data.url)
              })
              if (visibleEntries.length === 0) return null
              return (
                <div className="p-5 md:p-6 rounded-2xl bg-white border border-gray-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-4">Bereik</h3>
                  <div className="space-y-2.5">
                    {visibleEntries.map(([platform, data]) => {
                      const IconComponent = getSocialIcon(platform)
                      const followers = data.followers || data.subscribers
                      const content = (
                        <>
                          <IconComponent />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold capitalize text-sm truncate leading-tight">{platform}</p>
                            {followers ? (
                              <p className="text-xs text-[#94A3B8] mt-1">{followers.toLocaleString('nl-NL')} volgers</p>
                            ) : null}
                          </div>
                          {canVisitProfiles && (data.url || data.username) && <ExternalLink className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0" />}
                        </>
                      )
                      if (canVisitProfiles && (data.url || data.username)) {
                        return (
                          <a key={platform} href={data.url || `https://${platform}.com/${data.username}`} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-[#3EB489]/40 transition">
                            {content}
                          </a>
                        )
                      }
                      return (
                        <div key={platform} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200">
                          {content}
                        </div>
                      )
                    })}
                  </div>
                  <p className="mt-4 pt-4 border-t border-gray-100 text-[11px] text-[#94A3B8]">Contact loopt via UGC4you.</p>
                </div>
              )
            })()}

            {/* Info card: hoe het werkt */}
            <div className="p-5 md:p-6 rounded-2xl bg-white border border-gray-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="flex items-center gap-2 mb-5">
                <Zap className="w-4 h-4 text-[#3EB489]" />
                <h3 className="text-sm font-bold text-[#0F172A]">Hoe werkt het?</h3>
              </div>
              <ol className="space-y-4">
                {[
                  <><strong className="font-semibold text-[#0F172A]">Start gesprek</strong> of stel een opdracht voor</>,
                  <>Bespreek brief, budget en planning</>,
                  <>Bevestig met <strong className="font-semibold text-[#0F172A]">10% aanbetaling</strong></>,
                  <>Creator levert · 90% bij goedkeuring</>
                ].map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="w-6 h-6 rounded-full border border-[#3EB489]/30 text-[#3EB489] text-[11px] font-bold grid place-items-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-xs text-[#475569] leading-relaxed pt-1">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile Sticky CTA Bar */}
      {!propCreatorId && (
        <div className="fixed bottom-0 left-0 right-0 z-[60] lg:hidden bg-white border-t border-gray-200 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex gap-2">
            <button
              onClick={handleStartChat}
              disabled={startingChat}
              className="flex-1 min-h-[48px] px-4 py-3 bg-[#3EB489] text-white rounded-xl font-semibold text-sm hover:bg-[#359E78] disabled:opacity-60 transition flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              {startingChat ? 'Laden...' : 'Bericht'}
            </button>
            {isCompanyOrAgency && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="min-h-[48px] px-4 py-3 rounded-xl border border-gray-200 text-[#0F172A] font-semibold text-sm hover:border-[#3EB489] hover:text-[#3EB489] transition flex items-center justify-center gap-2"
              >
                <Briefcase className="w-4 h-4" />Opdracht
              </button>
            )}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      <InviteCreatorModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        creatorId={id}
        creatorName={
          creator?.firstName && creator?.lastName
            ? `${creator.firstName} ${creator.lastName}`
            : creator?.displayName || 'deze creator'
        }
      />
    </div>
  )
}

export default CreatorDetailPage
